import React, { useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { LuArrowUpDown, LuArrowUp, LuArrowDown, LuCopy, LuCheck } from 'react-icons/lu';
import { ScrollArea } from './ui/scroll-area';

/**
 * Flattens a nested object into dot-notation keys
 * e.g., { a: { b: 1 } } -> { 'a.b': 1 }
 */
const flattenObject = (obj, prefix = '') => {
  const result = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const newKey = prefix ? `${prefix}.${key}` : key;
      const value = obj[key];
      
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, flattenObject(value, newKey));
      } else {
        result[newKey] = value;
      }
    }
  }
  
  return result;
};

/**
 * Extracts schema (column names) from array of messages
 */
const extractSchema = (messages) => {
  const schemaSet = new Set(['timestamp', 'topic']);
  
  messages.forEach(msg => {
    // Add payload fields
    if (msg.payload && typeof msg.payload === 'object') {
      const flattened = flattenObject(msg.payload, 'payload');
      Object.keys(flattened).forEach(key => schemaSet.add(key));
    } else if (msg.payload !== undefined) {
      schemaSet.add('payload');
    }
    
    // Add any other top-level fields
    Object.keys(msg).forEach(key => {
      if (key !== 'payload' && key !== 'topic' && key !== 'timestamp') {
        schemaSet.add(key);
      }
    });
  });
  
  // Sort columns: timestamp, topic first, then payload fields, then others
  const columns = Array.from(schemaSet);
  const priority = { 'timestamp': 0, 'topic': 1 };
  
  return columns.sort((a, b) => {
    const pa = priority[a] ?? (a.startsWith('payload') ? 2 : 3);
    const pb = priority[b] ?? (b.startsWith('payload') ? 2 : 3);
    if (pa !== pb) return pa - pb;
    return a.localeCompare(b);
  });
};

/**
 * Gets nested value from object using dot notation
 */
const getNestedValue = (obj, path) => {
  const parts = path.split('.');
  let value = obj;
  
  for (const part of parts) {
    if (value === null || value === undefined) return undefined;
    value = value[part];
  }
  
  return value;
};

/**
 * Formats a cell value for display
 */
const formatCellValue = (value) => {
  if (value === null) return <span className="text-zinc-500 italic">null</span>;
  if (value === undefined) return <span className="text-zinc-600">-</span>;
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  if (typeof value === 'number') return value.toLocaleString();
  return String(value);
};

/**
 * Cell component with copy functionality
 */
const CopyableCell = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const displayValue = formatCellValue(value);
  const copyValue = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
  
  const handleCopy = async (e) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(copyValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  
  return (
    <div className="group flex items-center gap-1">
      <span className="truncate max-w-[200px]" title={copyValue}>{displayValue}</span>
      {value !== undefined && value !== null && (
        <button 
          onClick={handleCopy}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-white/10 rounded"
        >
          {copied ? <LuCheck className="w-3 h-3 text-emerald-500" /> : <LuCopy className="w-3 h-3 text-zinc-500" />}
        </button>
      )}
    </div>
  );
};

/**
 * Sortable column header
 */
const SortableHeader = ({ column, sortConfig, onSort }) => {
  const isActive = sortConfig.key === column;
  
  return (
    <button 
      onClick={() => onSort(column)}
      className="flex items-center gap-1 hover:text-white transition-colors w-full text-left"
    >
      <span className="truncate">{column}</span>
      {isActive ? (
        sortConfig.direction === 'asc' ? 
          <LuArrowUp className="w-3 h-3 text-cyan-500 flex-shrink-0" /> : 
          <LuArrowDown className="w-3 h-3 text-cyan-500 flex-shrink-0" />
      ) : (
        <LuArrowUpDown className="w-3 h-3 text-zinc-600 flex-shrink-0" />
      )}
    </button>
  );
};

/**
 * LiveDataTable - Displays live messages in a sortable table format
 */
export const LiveDataTable = ({ messages }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });
  
  // Extract schema from all messages
  const columns = useMemo(() => extractSchema(messages), [messages]);
  
  // Flatten and sort messages
  const tableData = useMemo(() => {
    const flattened = messages.map((msg, index) => {
      const row = { _index: index };
      
      // Add standard fields
      row.timestamp = msg.timestamp;
      row.topic = msg.topic;
      
      // Flatten payload
      if (msg.payload && typeof msg.payload === 'object') {
        const flatPayload = flattenObject(msg.payload, 'payload');
        Object.assign(row, flatPayload);
      } else if (msg.payload !== undefined) {
        row.payload = msg.payload;
      }
      
      // Add other fields
      Object.keys(msg).forEach(key => {
        if (key !== 'payload' && key !== 'topic' && key !== 'timestamp') {
          row[key] = msg[key];
        }
      });
      
      return row;
    });
    
    // Sort
    return flattened.sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return 1;
      if (bVal === undefined) return -1;
      
      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [messages, sortConfig]);
  
  const handleSort = (column) => {
    setSortConfig(prev => ({
      key: column,
      direction: prev.key === column && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };
  
  if (messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-zinc-500">
        <p>No data to display</p>
      </div>
    );
  }
  
  return (
    <div className="live-data-table">
      <div className="table-info">
        <span className="text-xs text-zinc-500">
          {messages.length} rows | {columns.length} columns
        </span>
      </div>
      <ScrollArea className="table-scroll-area">
        <Table>
          <TableHeader>
            <TableRow className="table-header-row">
              {columns.map(col => (
                <TableHead key={col} className="table-head-cell">
                  <SortableHeader 
                    column={col} 
                    sortConfig={sortConfig} 
                    onSort={handleSort} 
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableData.map((row, i) => (
              <TableRow key={row._index} className="table-body-row">
                {columns.map(col => (
                  <TableCell key={col} className="table-body-cell">
                    {col === 'timestamp' ? (
                      <span className="text-zinc-400 text-xs">
                        {new Date(row[col]).toLocaleTimeString()}
                      </span>
                    ) : (
                      <CopyableCell value={row[col]} />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
};

export default LiveDataTable;
